import React, { useMemo, useState } from 'react';
import {
  Stack,
  List,
  ListItem,
  Divider,
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Container,
  Tooltip,
} from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import SearchIcon from '@mui/icons-material/Search';
import avata from '../../Assets/Images/avata.png';
import usePhoneCall from '../../Hooks/useCallHandler';

function formatPhoneNumber(number) {
  if (!number) return '';
  const cleaned = number.replace(/\D/g, '');
  if (cleaned.length === 10 || cleaned.length === 11) {
    return cleaned.replace(/(\d{3})(\d{3,4})(\d{4})/, '$1-$2-$3');
  }
  return number;
}

export default function M_EMT_BASIC(props) {
  const { emTelList = [] } = props;
  const [search, setSearch] = useState('');
  const [isPressed, setIsPressed] = useState(false);
  const { handleCall } = usePhoneCall();

  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return emTelList;
    return emTelList.filter((it) => {
      return (
        (it.manageName || '').toString().toLowerCase().includes(q) ||
        (it.manageTel || '').toString().toLowerCase().includes(q) ||
        (it.managePosition || '').toString().toLowerCase().includes(q) ||
        (it.manageQualification || '').toString().toLowerCase().includes(q)
      );
    });
  }, [emTelList, search]);

  const Badge = ({ children, color = '#FDE6EF', textColor = '#C44A7A' }) => (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 1,
        py: 0.4,
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
        bgcolor: color,
        color: textColor,
        mr: 1,
      }}
    >
      {children}
    </Box>
  );

  return (
    <Container
      maxWidth="sm"
      sx={{
        mt: 0,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      <Stack spacing={0}>
        {/* 검색창 */}
        <Box sx={{ px: 1, mt: 0, mb: 0 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="검색 (이름, 전화번호, 직급, 자격)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ ml: 1 }}>
                  <SearchIcon sx={{ color: '#9AA6B2' }} />
                </InputAdornment>
              ),
              sx: {
                height: 32,
                padding: '0 8px',
              },
            }}
            sx={{
              bgcolor: '#fff',
              borderRadius: 1.5,
              '& .MuiOutlinedInput-input': {
                padding: '6px 0',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #E6EEF6',
              },
            }}
          />
        </Box>

        {/* 리스트 영역 */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <List sx={{ p: 0 }}>
            {filtered && filtered.length > 0 ? (
              filtered.map((item, idx) => (
                <React.Fragment key={idx}>
                  <ListItem
                    alignItems="center"
                    sx={{
                      px: 1,
                      py: 1.25,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                    disableGutters
                  >
                    {/* 텍스트 영역 클릭 시 전화번호 복사 */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        cursor: 'pointer',
                      }}
                      onTouchStart={() => setIsPressed(true)}
                      onTouchEnd={() => setIsPressed(false)}
                      onMouseDown={() => setIsPressed(true)}
                      onMouseUp={() => setIsPressed(false)}
                    >
                      {/* Avatar */}
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          backgroundColor: '#7B91A2',
                          borderRadius: '50%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Avatar src={avata} sx={{ width: 32, height: 32 }} />
                      </Box>

                      {/* Text area */}
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {item.rank && (
                          <Box sx={{ mb: 0.5 }}>
                            <Badge color="#FFEAF0" textColor="#D9416A">
                              {item.rank}
                            </Badge>
                          </Box>
                        )}

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 700, color: '#111' }}
                          >
                            {item.manageName}
                          </Typography>

                          {item.managePosition && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#6B7280',
                                fontSize: 12,
                                fontWeight: 500,
                                position: 'relative',
                                top: '2px',
                              }}
                            >
                              {item.managePosition}
                            </Typography>
                          )}
                        </Box>

                        <Typography
                          variant="body2"
                          sx={{ color: '#6B7280', mt: 0.5 }}
                        >
                          {formatPhoneNumber(item.manageTel)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* 통화 버튼 */}
                    <Tooltip
                      title={item.manageTel ? '전화 걸기' : '전화번호 없음'}
                    >
                      <IconButton
                        onClick={() => handleCall(item.manageTel)}
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          background: item.manageTel
                            ? 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)'
                            : '#ccc',
                          boxShadow: item.manageTel
                            ? '0 4px 12px rgba(46, 125, 50, 0.3)'
                            : 'none',
                          pointerEvents: item.manageTel ? 'auto' : 'none',
                          transition: 'all 0.1s ease-in-out',
                          transform: isPressed ? 'scale(0.92)' : 'scale(1)',
                          '&:hover': {
                            background: item.manageTel
                              ? 'linear-gradient(135deg, #43A047 0%, #1B5E20 100%)'
                              : '#ccc',
                            boxShadow: item.manageTel
                              ? '0 6px 16px rgba(27, 94, 32, 0.4)'
                              : 'none',
                          },
                          '&:active': {
                            transform: 'scale(0.92)',
                            boxShadow: item.manageTel
                              ? '0 2px 8px rgba(27, 94, 32, 0.6)'
                              : 'none',
                            background: item.manageTel
                              ? 'linear-gradient(135deg, #388E3C 0%, #1B5E20 100%)'
                              : '#ccc',
                          },
                        }}
                      >
                        <CallIcon sx={{ color: '#fff', fontSize: 22 }} />
                      </IconButton>
                    </Tooltip>
                  </ListItem>

                  {idx !== filtered.length - 1 && (
                    <Divider sx={{ mx: 1, borderColor: '#EEF2F7' }} />
                  )}
                </React.Fragment>
              ))
            ) : (
              <Box sx={{ px: 2, py: 6, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  등록된 비상연락처가 없습니다.
                </Typography>
              </Box>
            )}
          </List>
        </Box>
      </Stack>
    </Container>
  );
}
